import { useState, type FormEvent } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAdminAuth } from "@/hooks/useAdminAuth";

export default function LoginPage() {
  const { user, loading, login, resetPassword } = useAdminAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);

  const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ?? "/admin/dashboard";

  if (!loading && user) {
    navigate(from, { replace: true });
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    const result = await login(email, password);
    setSubmitting(false);

    if (result.success) {
      navigate(from, { replace: true });
    } else {
      setError(result.error ?? "Unable to sign in. Please try again.");
    }
  };

  const handleForgot = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const result = await resetPassword(forgotEmail);
    setSubmitting(false);
    if (result.success) {
      setForgotSent(true);
    } else {
      setError(result.error ?? "Unable to send reset email. Please try again.");
    }
  };

  return (
    <div className="min-h-screen flex bg-background-950">
      <div className="hidden lg:flex flex-1 flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-background-900 via-background-950 to-primary-900" />
        <div className="relative z-10 flex items-center gap-3">
          <span className="w-11 h-11 flex items-center justify-center rounded-md bg-accent-500 text-white font-extrabold text-lg">
            L+
          </span>
          <div className="leading-tight">
            <p className="text-white font-bold text-xl tracking-wide">L+TV TÉLÉVISION</p>
            <p className="text-background-300 text-xs uppercase tracking-widest">Administration</p>
          </div>
        </div>

        <div className="relative z-10 max-w-md">
          <h1 className="text-3xl md:text-4xl font-bold text-white leading-tight">
            The central control system for your television network.
          </h1>
          <p className="mt-4 text-background-300 text-base leading-relaxed">
            Manage news, live broadcasting, videos, programs, schedules, users and website settings —
            all from one professional dashboard.
          </p>
        </div>

        <p className="relative z-10 text-background-400 text-sm">
          © {new Date().getFullYear()} L+TV TÉLÉVISION — Edea, Cameroon
        </p>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 bg-background-50">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <span className="w-10 h-10 flex items-center justify-center rounded-md bg-accent-500 text-white font-extrabold">
              L+
            </span>
            <div className="leading-tight">
              <p className="font-bold text-foreground-900">L+TV TÉLÉVISION</p>
              <p className="text-[11px] uppercase tracking-widest text-foreground-500">Administration</p>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-foreground-900">
            {forgotMode ? "Reset your password" : "Sign in to your account"}
          </h2>
          <p className="mt-1 text-sm text-foreground-600">
            {forgotMode
              ? "Enter your email and we'll send you a reset link."
              : "Enter your credentials to access the dashboard."}
          </p>

          {forgotMode ? (
            <form onSubmit={handleForgot} className="mt-8 space-y-4">
              {forgotSent ? (
                <div className="flex items-start gap-2 px-3 py-3 bg-green-50 border border-green-200 rounded-md">
                  <i className="ri-checkbox-circle-line text-green-600 text-sm mt-0.5" />
                  <p className="text-sm text-green-700">
                    If an account exists for that email, a reset link has been sent. Check your inbox.
                  </p>
                </div>
              ) : (
                <div>
                  <label htmlFor="forgotEmail" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    Email
                  </label>
                  <input
                    id="forgotEmail"
                    type="email"
                    name="email"
                    autoComplete="email"
                    required
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    placeholder="you@ltv.tv"
                    className="w-full px-4 py-3 bg-white border border-background-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                  />
                </div>
              )}

              {error && (
                <div className="flex items-start gap-2 px-3 py-2.5 bg-accent-50 border border-accent-200 rounded-md">
                  <i className="ri-error-warning-line text-accent-600 text-sm mt-0.5" />
                  <p className="text-sm text-accent-700">{error}</p>
                </div>
              )}

              {!forgotSent && (
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full flex items-center justify-center gap-2 bg-primary-600 text-white font-semibold px-5 py-3 rounded-md hover:bg-primary-700 transition-colors disabled:opacity-60 whitespace-nowrap"
                >
                  {submitting && <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
                  Send Reset Link
                </button>
              )}

              <button
                type="button"
                onClick={() => {
                  setForgotMode(false);
                  setForgotSent(false);
                  setError("");
                }}
                className="w-full text-center text-sm text-primary-600 hover:underline whitespace-nowrap"
              >
                Back to sign in
              </button>
            </form>
          ) : (
            <form onSubmit={handleSubmit} className="mt-8 space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                Email
              </label>
              <input
                id="email"
                type="email"
                name="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@ltv.tv"
                className="w-full px-4 py-3 bg-white border border-background-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-foreground-800 mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full px-4 py-3 pr-11 bg-white border border-background-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-foreground-400"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  <i className={showPassword ? "ri-eye-off-line text-base" : "ri-eye-line text-base"} />
                </button>
              </div>
            </div>

            <div className="flex items-center justify-end">
              <button
                type="button"
                onClick={() => setForgotMode(true)}
                className="text-sm text-primary-600 hover:underline whitespace-nowrap cursor-pointer"
              >
                Forgot password?
              </button>
            </div>

            {error && (
              <div className="flex items-start gap-2 px-3 py-2.5 bg-accent-50 border border-accent-200 rounded-md">
                <i className="ri-error-warning-line text-accent-600 text-sm mt-0.5" />
                <p className="text-sm text-accent-700">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full flex items-center justify-center gap-2 bg-primary-600 text-white font-semibold px-5 py-3 rounded-md hover:bg-primary-700 transition-colors disabled:opacity-60 whitespace-nowrap"
            >
              {submitting && <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />}
              Sign In
            </button>
          </form>
          )}
        </div>
      </div>
    </div>
  );
}